<script>
	import { labels, clusters, layout } from "./utils/globals";
	import { createEventDispatcher, onMount, tick } from "svelte";
	import Edges from "./models/Edges";
	import {
		Color,
		MOUSE,
		PerspectiveCamera,
		Scene,
		TOUCH,
		Vector2,
		Vector3,
		WebGLRenderer,
	} from "three";
	import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
	import Thumbnails from "./models/Thumbnails";
	import { clamp, distance, getParameterByName, norm } from "./utils/utils";
	import Isoline from "./models/Isoline";
	import { analytics } from "./utils/Analytics";

	export let hidden = false;

	const dispatch = createEventDispatcher();
	// scale of the meshes, also used to upscale the labels' coordinates:  
	const downscale = 0.02;
	const maxDistance = 50;

	let w, h;
	let cancelClick;
	let cancelTimeout;
	let container;
	let controller;
	let edges;
	let follow;
	let tmpColor;
	let inactive;
	let isHovering = false;
	let thumbnails;
	let camera;
	let renderer;
	let scene;
	let isoline;
	let selectedCluster;
	let targetZ;

	onMount(() => {
		w = window.innerWidth;
		h = window.innerHeight;

		tmpColor = new Color()
		scene = new Scene();

		renderer = new WebGLRenderer({
			antialias: false,
			logarithmicDepthBuffer: true,
		});
		renderer.setClearColor(new Color(1, 1, 1), 1);

		camera = new PerspectiveCamera(60, w / h, 0.0001, 100);
		camera.position.z = targetZ = 40;
		camera.lookAt(new Vector3());
		
		controller = new OrbitControls(camera, container);
		controller.mouseButtons = {
			LEFT: MOUSE.PAN,
			MIDDLE: MOUSE.DOLLY,
			RIGHT: MOUSE.PAN,
		};
		controller.minDistance = 5;
		controller.maxDistance = maxDistance;
		controller.touches = {
			ONE: TOUCH.PAN,
			TWO: TOUCH.DOLLY_PAN,
		};
		controller.enableDamping = false;
		controller.dampingFactor = 0.05;
		controller.addEventListener("start", () => {
			clearTimeout(cancelTimeout);
			cancelTimeout = setTimeout(() => {
				cancelClick = true;
			}, 200);
			inactive = false;
		});
		controller.addEventListener("end", () => {
			clearTimeout(cancelTimeout);
			cancelTimeout = setTimeout(() => {
				cancelClick = false;
			}, 100);
		});

		container.appendChild(renderer.domElement);
		resize();
		render();
	});

	// TODO merge method createEdges?
	function createEdges() { 
		edges = new Edges();
		edges.scale.multiplyScalar(downscale);
		scene.add(edges);
		isoline = new Isoline( thumbnails, edges, camera, scene, renderer )
	}

	export function createThumbnails( texture, mask) {
		thumbnails = new Thumbnails(texture, mask);
		thumbnails.scale.multiplyScalar(downscale);
		scene.add(thumbnails);
		createEdges();
	}

	export function focusOn(pos, disableZoom = false) {
		follow = pos;
		controller.enablePan = false;
		controller.enableZoom = !disableZoom;
		inactive = true;
	}

	export function getCameraRatio(min = 3, max = 5, k = 4) {
		const x = clamp(0, norm(camera.position.z, min, max), 1);
		const a = 0.5 * Math.pow(2.0 * (x < 0.5 ? x : 1.0 - x), k);
		return clamp(0, x < 0.5 ? a : 1.0 - a, 1);
	}

	// why is it triggered twice when a cluster ??
	function getClosestItem(e) {
		if (cancelClick) return;
		let mouse = { x: e.pageX, y: e.pageY };
		if (e.type === "touchend") {
			mouse.x = e.changedTouches[0].pageX;
			mouse.y = e.changedTouches[0].pageY;
		}
		let min = Infinity;
		let closest = null;
		let type = "";
		let isFromSelectedCluster = false;
		$clusters.forEach((cluster) => {
			const position = getScreenPositionOf(cluster.body.pos);
			let d = distance(mouse, position);
			if (d < min) {
				min = d;
				closest = cluster;
				type = "cluster";
			}
			cluster.nodes.forEach((node) => {
				const position = getScreenPositionOf(node.body.pos);
				let d = distance(mouse, position);
				if (d < min) {
					min = d;
					closest = node;
					isFromSelectedCluster = selectedCluster === cluster;
					type = "node";
				}
			});
		});
		const maxSelectDistance = 10 
															- (1 - camera.position.z / maxDistance) * 10
															+ maxDistance - camera.position.z;
		if (min > maxSelectDistance) {
			if (e.type === "click" || e.type === "touchend") {
				unfocus();
			} else {
				dispatch("deselect", true);
			}
			isHovering = false;
		} else {
			if (type === "node") {
				dispatch("selectItem", {
					item: closest,
					hover: e.type !== "click" && e.type !== "touchend"
				});
			} else {
				dispatch("selectCluster", {
					cluster: closest,
					hover: e.type !== "click" && e.type !== "touchend"
				});
			}
			if (e.type === "click" || e.type === "touchend") {
				if (type === "node") {
					if (!isFromSelectedCluster) {
						selectedCluster = null;
					}
				} else {
					selectedCluster = closest;
					thumbnails.cluster = selectedCluster.uid
					analytics.clickCluster(selectedCluster.text)
				}
				focusOn(closest.body.pos, type === "node");
			} else {
				isHovering = true;
			}
		}
	}

	export function getScreenPositionOf(pos) {
		const projection = new Vector3(pos.x, pos.y, pos.z || 0);
		projection.multiplyScalar(downscale);
		projection.project(camera);
		projection.x = (projection.x + 1) * w / 2;
		projection.y = -(projection.y - 1) * h / 2;
		return projection;
	}

	//prepare the simulation for the initial view
	//(edges and thumbnails already exist)
	export function precomputeLayout(steps) {
		//fast forward the layout simulation
		layout.springLength = 0.0001
		while( steps--) layout.step()
		//place camera at:
		zoomTo(5);
		//set the thumbnails to show the image
		// 0 = pie charts
		// 1 = image
		// or use the camera distance: getCameraRatio(14, 10, 2)
		thumbnails.update( 1 );
		render();
	}

	function render() {
		controller.update();
		if( isoline && selectedCluster ){
			isoline.render(selectedCluster)
		}else{
			renderer.render(scene, camera);
		} 
	}

	export function resize() {
		w = window.innerWidth;
		h = window.innerHeight;
		renderer.setSize(w, h);
		renderer.setPixelRatio(1);
		camera.aspect = w / h;
		camera.updateProjectionMatrix();
		if( isoline ) isoline.resize(w,h)
	}

	export function step(zoomRatio=0) {
		if (follow) {
			controller.target.x += (follow.x * downscale - controller.target.x) * .1;
			controller.target.y += (follow.y * downscale - controller.target.y) * .1;
			if (inactive) {
				camera.position.z += (10 - camera.position.z) * .1;
			}
		} else if (inactive) {
			controller.target.x *= .95;
			controller.target.y *= .95;
			// if (inactive) {
				camera.position.z += (targetZ - camera.position.z) * .05;
			// }
		}
		camera.position.x += (controller.target.x - camera.position.x) * .3;
		camera.position.y += (controller.target.y - camera.position.y) * .3;
		edges && edges.update( zoomRatio );
		thumbnails && thumbnails.update(getCameraRatio(14, 10, 2));
		render();
	}

	export function unfocus(focusOnSelectedCluster = false) {
		if (focusOnSelectedCluster && selectedCluster) {
			focusOn(selectedCluster.body.pos);
			dispatch("selectCluster", { cluster: selectedCluster });
		} else {
			selectedCluster = null;
			follow = null;
			thumbnails.cluster = -1
			controller.enablePan = true;
			controller.enableZoom = true;
			edges.visible = false;
			zoomTo(window.innerWidth < 800 ? 20 : 40);
			dispatch("deselect");
		}
	}

	export function zoomTo(z) {
		inactive = true;
		targetZ = z;
	}
</script>

<div class:hidden
	class:dragging={cancelClick}
	class:hovering={!cancelClick && isHovering}
	bind:this={container}
	on:click={getClosestItem}
	on:touchend={getClosestItem}
	on:mousemove={getClosestItem}></div>

<style>
	div {
		cursor: -webkit-grab;
		cursor: -moz-grab;
		cursor: grab;
		transition: opacity 1s ease-in-out;
	}

	div.dragging {
		cursor: -webkit-grabbing;
		cursor: -moz-grabbing;
		cursor: grabbing;
	}

	div.hovering {
		cursor: pointer;
	}

	div.hidden {
		opacity: 0;
	}
</style>